import React, { useEffect, useMemo } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { AppLogo } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import { LANDING_LOGIN_BUTTON, LOGO_TEST_ID } from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { endSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import { RegularStorage, StorageKeys } from 'services/mmkv';

import useDebouncedSafeAreaInsets from 'utils/hooks/useDebouncedSafeAreaInsets';
import { isWeb, widthPercentageToDP } from 'utils/responsiveness';

import ActionButton from 'components/CustomButtons/ActionButton';
import LocalizedText from 'components/LocalizedText';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles, { webStyles } from './styles';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.ROOT>;
};

const {
    WELCOME_SCREEN_HEADER,
    WELCOME_SCREEN_LOGIN_BUTTON,
    WELCOME_SCREEN_SUBHEADER,
} = LANGUAGE_KEYS;

const WelcomeScreen = ({ navigation }: Props) => {
    const dispatch = useDispatch();
    const { bottom: bottomInset } = useDebouncedSafeAreaInsets();
    const { t } = useTranslation();
    const appLogoSize = widthPercentageToDP(64, 80, 80);

    const { background, primary } = useSelector(selectLayoutConfig);

    const continuePressed = async () => {
        navigation.reset({
            routes: [{ name: SCREEN.LOGIN_SCREEN }],
        });
    };

    useEffect(() => {
        dispatch(
            endSentryTransaction(SENTRY_TRANSACTIONS.AppStartToWelcomePage),
        );

        RegularStorage.setItem(
            StorageKeys.LANDING_PAGE_SHOWN,
            JSON.stringify(true),
        ).catch((error) => {
            console.error('Cant save LANDING_PAGE_SHOWN true', error);
        });
    }, []);

    const landingStyles = useMemo(() => {
        if (isWeb) {
            return {
                ...styles,
                ...webStyles,
            };
        }

        return styles;
    }, []);

    return (
        <View
            style={[
                gStyles.container,
                {
                    backgroundColor: background,
                    marginBottom: Math.max(24, bottomInset),
                },
            ]}
        >
            <View style={landingStyles.content}>
                <View style={landingStyles.logoImage}>
                    <AppLogo
                        testID={LOGO_TEST_ID}
                        width={appLogoSize}
                        height={appLogoSize}
                        role="img"
                        accessibilityRole="image"
                        aria-label="OneTV logo"
                        tabIndex={0}
                    />
                </View>
                <Text
                    category={ETextCategory.title3}
                    style={landingStyles.title}
                    color={ETextColor.high_contrast}
                    accessibilityLabel={t(WELCOME_SCREEN_HEADER)}
                    role="heading"
                    accessibilityRole="header"
                    // @ts-ignore
                    tabIndex={0}
                    bold
                >
                    <LocalizedText
                        textKey={WELCOME_SCREEN_HEADER}
                        // @ts-ignore
                        style={landingStyles.textStyle}
                    />
                </Text>
                <Text
                    category={ETextCategory.body1}
                    color={ETextColor.high_contrast}
                    accessibilityLabel={t(WELCOME_SCREEN_SUBHEADER)}
                    // @ts-ignore
                    tabIndex={0}
                >
                    <LocalizedText
                        textKey={WELCOME_SCREEN_SUBHEADER}
                        // @ts-ignore
                        style={[
                            landingStyles.textStyle,
                            landingStyles.descriptionStyle,
                        ]}
                        accessible
                    />
                </Text>
                <ActionButton
                    testID={LANDING_LOGIN_BUTTON}
                    title={WELCOME_SCREEN_LOGIN_BUTTON}
                    onPress={continuePressed}
                    fillColor={primary}
                    buttonStyle={landingStyles.submitButtonStyle}
                    buttonTextStyle={{ color: background }}
                    accessibilityLabel={t(WELCOME_SCREEN_LOGIN_BUTTON)}
                />
            </View>
        </View>
    );
};

export default WelcomeScreen;
